import React, { useMemo } from 'react'
import { Button } from '../../../components/Button/Button'
import { CrossClientQueryAtomicClause, CrossClientQueryResultSummary } from '../../../models/CrossClient'
import { type CrossClientReviewState } from './hooks'

interface CrossClientChatReviewProps {
  reviewState: CrossClientReviewState
}

type PromptTone = 'warning' | 'danger'

type TimelineItem =
  | { kind: 'done'; key: string; content: React.ReactNode }
  | { kind: 'criteria'; key: string; clauses: CrossClientQueryAtomicClause[] }
  | { kind: 'working'; key: string; text: string }
  | { kind: 'prompt'; key: string; tone: PromptTone; text: string }

export const CrossClientChatReview: React.FC<CrossClientChatReviewProps> = ({ reviewState }) => {
  const items = useMemo(() => buildTimeline(reviewState), [reviewState])

  return (
    <div className='d-flex flex-column gap-3 cross-client-breakdown'>
      {items.map((item) => (
        <TimelineRow key={item.key} item={item} />
      ))}
    </div>
  )
}

interface CrossClientChatReviewActionsProps {
  reviewState: CrossClientReviewState
  onConfirm: () => void
  onRestart: () => void
  isLoading: boolean
}

export const CrossClientChatReviewActions: React.FC<CrossClientChatReviewActionsProps> = ({ reviewState, onConfirm, onRestart, isLoading }) => {
  const variant = getReviewVariant(reviewState)
  if (!variant.showAnyAction) {
    return null
  }
  return (
    <div className='d-flex gap-2'>
      <Button variant='header' className='button-medium' onClick={onRestart} disabled={isLoading}>
        Refine question
      </Button>
      {variant.showRunQuery ? (
        <Button variant='primary' className='button-medium' onClick={onConfirm} loading={isLoading}>
          Run query
        </Button>
      ) : null}
    </div>
  )
}

const TimelineRow: React.FC<{ item: TimelineItem }> = ({ item }) => {
  switch (item.kind) {
    case 'done':
      return (
        <div className='position-relative d-flex align-items-center zocks-gap-0.75 cross-client-breakdown__step'>
          <div className='d-inline-flex align-items-center justify-content-center cross-client-breakdown__bullet cross-client-breakdown__bullet--done'>
            <i className='bi bi-check' />
          </div>
          <div className='paragraph-medium mb-0'>{item.content}</div>
        </div>
      )
    case 'criteria':
      return (
        <ul className='position-relative d-flex flex-column gap-3 cross-client-breakdown__criteria'>
          {item.clauses.map((clause) => (
            <li key={clause.variable} className='d-flex align-items-center gap-2 cross-client-breakdown__criterion'>
              <i className='bi bi-x-diamond-fill cross-client-breakdown__criterion-icon' />
              <span>{clause.questionForm || clause.description}</span>
            </li>
          ))}
        </ul>
      )
    case 'working':
      return (
        <div className='position-relative d-flex align-items-center zocks-gap-0.75 cross-client-breakdown__step cross-client-breakdown__step--working'>
          <div className='d-inline-flex align-items-center justify-content-center cross-client-breakdown__bullet cross-client-breakdown__bullet--working' />
          <p className='paragraph-medium mb-0 shimmering-loader-text'>{item.text}</p>
        </div>
      )
    case 'prompt':
      return (
        <div className='position-relative d-flex align-items-center zocks-gap-0.75 cross-client-breakdown__step'>
          <div
            className={`d-inline-flex align-items-center justify-content-center cross-client-breakdown__bullet cross-client-breakdown__bullet--prompt cross-client-breakdown__bullet--prompt-${item.tone}`}
          />
          <p className='paragraph-medium mb-0'>{item.text}</p>
        </div>
      )
  }
}

interface ReviewVariant {
  showAnyAction: boolean
  showRunQuery: boolean
}

const getReviewVariant = (state: CrossClientReviewState): ReviewVariant => {
  switch (state.kind) {
    case 'deep-only':
      return { showAnyAction: true, showRunQuery: true }
    case 'instant-running':
    case 'instant-matches':
      return { showAnyAction: false, showRunQuery: false }
    case 'instant-no-matches':
      return classifyNoMatchSummary(state.resultSummary).showRunQuery
        ? { showAnyAction: true, showRunQuery: true }
        : { showAnyAction: true, showRunQuery: false }
  }
}

const classifyNoMatchSummary = (
  resultSummary: CrossClientQueryResultSummary,
): { copy: string; tone: PromptTone; promptText: string; doneText: string; showRunQuery: boolean } => {
  if (resultSummary.noMatchCount === resultSummary.totalCount) {
    return {
      copy: 'all-no-match',
      tone: 'danger',
      promptText: 'Your query didn’t match any clients. Try refining your question.',
      doneText: `Checked all ${resultSummary.totalCount.toLocaleString()} ${resultSummary.totalCount === 1 ? 'client' : 'clients'}.`,
      showRunQuery: false,
    }
  }
  if (resultSummary.partialCount === resultSummary.totalCount) {
    return {
      copy: 'all-partial',
      tone: 'warning',
      promptText: 'Want me to use AI and external sources to run deep analysis?',
      doneText: 'Found partial matches only.',
      showRunQuery: true,
    }
  }
  return {
    copy: 'mixed',
    tone: 'warning',
    promptText: 'Want me to use AI and external sources to run deep analysis?',
    doneText: 'No instant matches found.',
    showRunQuery: true,
  }
}

const buildTimeline = (state: CrossClientReviewState): TimelineItem[] => {
  const clauses = state.atomicClauses
  const count = clauses.length
  const noun = count === 1 ? 'criterion' : 'criteria'

  switch (state.kind) {
    case 'deep-only':
      return [
        {
          kind: 'done',
          key: 'breakdown',
          content: `${count} ${noun} found. ${count === 1 ? 'It requires' : 'They require'} deeper analysis using AI and external sources.`,
        },
        { kind: 'criteria', key: 'criteria', clauses },
        { kind: 'prompt', key: 'ready', tone: 'warning', text: 'Ready to run this query?' },
      ]
    case 'instant-running':
      return [
        { kind: 'done', key: 'breakdown', content: `${count} ${noun} found. Some of them can be checked instantly.` },
        { kind: 'criteria', key: 'criteria', clauses },
        { kind: 'working', key: 'instant', text: 'Running instant queries' },
      ]
    case 'instant-matches': {
      const { fullCount, partialCount } = state.resultSummary
      return [
        { kind: 'done', key: 'breakdown', content: `${count} ${noun} found. Some of them can be checked instantly.` },
        { kind: 'criteria', key: 'criteria', clauses },
        {
          kind: 'done',
          key: 'matches',
          content: (
            <>
              <strong>{fullCount} full</strong> {fullCount === 1 ? 'match' : 'matches'}
              {partialCount > 0 ? (
                <>
                  , <strong>{partialCount} partial</strong> {partialCount === 1 ? 'match' : 'matches'}
                </>
              ) : null}{' '}
              found
            </>
          ),
        },
        { kind: 'working', key: 'opening', text: 'Opening results' },
      ]
    }
    case 'instant-no-matches': {
      const { tone, promptText, doneText } = classifyNoMatchSummary(state.resultSummary)
      return [
        { kind: 'done', key: 'breakdown', content: `${count} ${noun} found. Some of them can be checked instantly.` },
        { kind: 'criteria', key: 'criteria', clauses },
        { kind: 'done', key: 'outcome', content: doneText },
        { kind: 'prompt', key: 'ready', tone, text: promptText },
      ]
    }
  }
}
